import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { RichTextEditor } from "../components/RichTextEditor";
import { safeHtmlProps } from "../utils/sanitizeHtml";
import { Plus, PencilSimple, Trash, Star, X, Check, UploadSimple, FileText, Receipt, FileArrowDown, Copy } from "@phosphor-icons/react";

const DOC_TYPES = [
  { key: "estimation", label: "Quotation", icon: FileText },
  { key: "proforma", label: "Proforma", icon: FileArrowDown },
  { key: "invoice", label: "Invoice", icon: Receipt },
];

const emptyTemplate = (doc_type) => ({
  doc_type,
  name: "",
  is_default: false,
  subject_prefix: "",
  notes_html: "",
  terms_html: "",
  footer_html: "",
  header_html: "",
  validity_days: 15,
  letterhead_data_url: "",
  letterhead_opacity: 0.08,
  accent_color: "",
  show_hsn: true,
  show_discount: true,
  show_bank: true,
  show_signature: true,
  show_amount_words: true,
  show_qr: false,
});

/* -------- Templates Tab (mounts under Billing "Templates" tab) -------- */
export const TemplatesTab = ({ user }) => {
  const [activeDocType, setActiveDocType] = useState("estimation");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // { mode, data }

  const canEdit = ["admin", "manager"].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/doc-templates?doc_type=${activeDocType}&summary=true`);
      setTemplates(r.data || []);
    } catch { toast.error("Failed to load templates"); }
    finally { setLoading(false); }
  }, [activeDocType]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => setEditor({ mode: "create", data: emptyTemplate(activeDocType) });

  const openEdit = async (t) => {
    // Fetch full template (including heavy letterhead_data_url)
    try {
      const r = await axios.get(`${API}/doc-templates/${t.template_id}`);
      setEditor({ mode: "edit", data: r.data });
    } catch { toast.error("Failed to load template"); }
  };

  const setDefault = async (t) => {
    try {
      await axios.post(`${API}/doc-templates/set-default/${t.template_id}?requester_role=${user.role}`);
      toast.success(`"${t.name}" set as default`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try {
      await axios.delete(`${API}/doc-templates/${t.template_id}?requester_role=${user.role}`);
      toast.success("Template deleted");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const duplicate = async (t) => {
    try {
      const r = await axios.post(`${API}/doc-templates/${t.template_id}/duplicate?requester_role=${user.role}`);
      toast.success(`Duplicated as "${r.data.name}"`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="templates-tab">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex gap-1 border border-slate-200 rounded p-0.5 bg-slate-50">
          {DOC_TYPES.map(dt => {
            const I = dt.icon;
            return (
              <button key={dt.key} data-testid={`tmpl-type-${dt.key}`} onClick={() => setActiveDocType(dt.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded transition-all ${activeDocType === dt.key ? "bg-white shadow text-[#002FA7]" : "text-slate-600 hover:text-slate-900"}`}>
                <I size={14} weight={activeDocType === dt.key ? "fill" : "regular"} /> {dt.label}
              </button>
            );
          })}
        </div>
        {canEdit && (
          <button data-testid="tmpl-new-btn" onClick={openCreate}
            className="rounded bg-[#002FA7] text-white hover:bg-[#00227A] px-4 py-2 text-sm font-bold flex items-center gap-2">
            <Plus size={16} weight="bold" /> New Template
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="loading-spinner"></div></div>
      ) : templates.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded p-10 text-center">
          <FileText size={36} className="mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500 mb-3">No {DOC_TYPES.find(d => d.key === activeDocType)?.label} templates yet.</p>
          {canEdit && <button onClick={openCreate} className="text-sm text-[#002FA7] font-bold hover:underline">Create your first template →</button>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(t => (
            <div key={t.template_id} data-testid={`tmpl-card-${t.template_id}`}
              className={`border rounded p-4 bg-white hover:shadow-md transition-all ${t.is_default ? "border-[#002FA7] border-2" : "border-slate-200"}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-base" style={{fontFamily: 'Outfit, sans-serif'}}>{t.name}</h3>
                    {t.is_default && <span className="text-[10px] uppercase tracking-wider bg-[#002FA7] text-white px-1.5 py-0.5 rounded">Default</span>}
                  </div>
                  {t.subject_prefix && <p className="text-xs text-slate-500 mt-0.5">Subject: {t.subject_prefix}…</p>}
                </div>
              </div>
              <div className="text-xs text-slate-600 line-clamp-2 mb-3" {...safeHtmlProps(t.notes_html || "<em class='text-slate-400'>No notes</em>")} />
              <div className="flex items-center gap-2 text-[11px] text-slate-500 mb-3">
                <span>📅 Valid {t.validity_days}d</span>
                {t.show_bank && <span>🏦 Bank</span>}
                {t.show_qr && <span>📱 QR</span>}
                {t.show_signature && <span>✍ Sign</span>}
              </div>
              <div className="flex items-center gap-1 pt-3 border-t border-slate-100">
                <button onClick={() => openEdit(t)} className="flex-1 px-2 py-1.5 text-xs text-[#002FA7] font-bold hover:bg-slate-50 rounded flex items-center justify-center gap-1" data-testid={`tmpl-edit-${t.template_id}`}>
                  <PencilSimple size={12} /> Edit
                </button>
                {canEdit && (
                  <button onClick={() => duplicate(t)} className="flex-1 px-2 py-1.5 text-xs text-slate-700 font-bold hover:bg-slate-50 rounded flex items-center justify-center gap-1" data-testid={`tmpl-duplicate-${t.template_id}`} title="Clone this template">
                    <Copy size={12} /> Duplicate
                  </button>
                )}
                {canEdit && !t.is_default && (
                  <button onClick={() => setDefault(t)} className="flex-1 px-2 py-1.5 text-xs text-amber-600 font-bold hover:bg-amber-50 rounded flex items-center justify-center gap-1" data-testid={`tmpl-set-default-${t.template_id}`}>
                    <Star size={12} /> Default
                  </button>
                )}
                {canEdit && (
                  <button onClick={() => remove(t)} className="px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded" data-testid={`tmpl-delete-${t.template_id}`}>
                    <Trash size={12} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editor && <TemplateEditorModal editor={editor} setEditor={setEditor} user={user} onSaved={() => { setEditor(null); load(); }} />}
    </div>
  );
};

/* -------- Template Editor Modal -------- */
const TemplateEditorModal = ({ editor, setEditor, user, onSaved }) => {
  const [form, setForm] = useState(editor.data);
  const [tab, setTab] = useState("content"); // content | layout | preview
  const [saving, setSaving] = useState(false);

  const upd = (patch) => setForm(f => ({ ...f, ...patch }));

  const handleLetterhead = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Letterhead must be under 3 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => upd({ letterhead_data_url: reader.result });
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Template name required"); return; }
    if (!form.doc_type) { toast.error("Doc type required"); return; }
    setSaving(true);
    try {
      if (editor.mode === "create") {
        await axios.post(`${API}/doc-templates?requester_role=${user.role}`, form);
        toast.success("Template created");
      } else {
        await axios.put(`${API}/doc-templates/${form.template_id}?requester_role=${user.role}`, form);
        toast.success("Template updated");
      }
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setEditor(null)}>
      <div data-testid="template-editor-modal" className="bg-white shadow-2xl max-w-6xl w-full my-4" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex justify-between items-center z-20">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">{editor.mode === "create" ? "New" : "Edit"} Template · {form.doc_type}</div>
            <h2 className="text-xl font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>{form.name || "Untitled template"}</h2>
          </div>
          <button onClick={() => setEditor(null)} className="p-2 hover:bg-slate-100 rounded-full" aria-label="Close"><X size={20} /></button>
        </div>

        <div className="flex gap-1 border-b border-slate-200 px-6 pt-2">
          {["content", "layout", "preview"].map(t => (
            <button key={t} data-testid={`tmpl-subtab-${t}`} onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium capitalize transition-all border-b-2 -mb-px ${tab === t ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              {t}
            </button>
          ))}
        </div>

        <div className="p-6">
          {tab === "content" && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600 block mb-1">Template Name <span className="text-red-500">*</span></label>
                  <input data-testid="tmpl-name" value={form.name} onChange={e => upd({ name: e.target.value })} placeholder="e.g. Corporate Quote" className="w-full rounded border border-slate-300 focus:border-[#002FA7] p-2 outline-none text-sm" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600 block mb-1">Doc Type</label>
                  <select value={form.doc_type} onChange={e => upd({ doc_type: e.target.value })} disabled={editor.mode === "edit"}
                    className="w-full rounded border border-slate-300 focus:border-[#002FA7] p-2 outline-none text-sm bg-white disabled:opacity-60">
                    {DOC_TYPES.map(dt => <option key={dt.key} value={dt.key}>{dt.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600 block mb-1">Validity (days)</label>
                  <input type="number" min="1" value={form.validity_days} onChange={e => upd({ validity_days: Number(e.target.value) })}
                    className="w-full rounded border border-slate-300 focus:border-[#002FA7] p-2 outline-none text-sm" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600 block mb-1">Subject Prefix</label>
                  <input value={form.subject_prefix || ""} onChange={e => upd({ subject_prefix: e.target.value })} placeholder='e.g. "Quotation for "' className="w-full rounded border border-slate-300 focus:border-[#002FA7] p-2 outline-none text-sm" />
                  <div className="text-[11px] text-slate-500 mt-0.5">Auto-prepended to the Subject field on new docs.</div>
                </div>
                <label className="flex items-center gap-2 text-sm mt-5">
                  <input type="checkbox" checked={form.is_default} onChange={e => upd({ is_default: e.target.checked })} data-testid="tmpl-is-default" />
                  <span>Set as default for {form.doc_type}</span>
                </label>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600 block mb-1">Header (optional — shows above Bill To)</label>
                <RichTextEditor value={form.header_html} onChange={v => upd({ header_html: v })} placeholder="Optional intro or reference line…" minHeight={80} testId="tmpl-header" />
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600 block mb-1">Customer Notes</label>
                <RichTextEditor value={form.notes_html} onChange={v => upd({ notes_html: v })} placeholder="Thanks for your business…" testId="tmpl-notes" />
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600 block mb-1">Terms &amp; Conditions</label>
                <RichTextEditor value={form.terms_html} onChange={v => upd({ terms_html: v })} placeholder="Payment terms, warranty, delivery…" testId="tmpl-terms" />
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600 block mb-1">Footer</label>
                <RichTextEditor value={form.footer_html} onChange={v => upd({ footer_html: v })} placeholder="Bank · contact · tagline…" minHeight={80} testId="tmpl-footer" />
              </div>
            </div>
          )}

          {tab === "layout" && (
            <div className="space-y-5">
              <div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-slate-600 mb-2">Letterhead Image</div>
                <p className="text-xs text-slate-500 mb-3">Uploaded image will appear behind the doc content at reduced opacity — perfect for pre-printed letterhead look.</p>
                <div className="flex items-start gap-4">
                  <label className="inline-flex items-center gap-2 cursor-pointer rounded border border-slate-300 px-3 py-2 hover:bg-slate-50 text-sm">
                    <UploadSimple size={16} />
                    <span>{form.letterhead_data_url ? "Replace letterhead" : "Upload letterhead"}</span>
                    <input type="file" accept="image/*" className="hidden" onChange={handleLetterhead} data-testid="tmpl-letterhead-upload" />
                  </label>
                  {form.letterhead_data_url && (
                    <button onClick={() => upd({ letterhead_data_url: "" })} className="text-xs text-red-600 hover:underline self-center">Remove</button>
                  )}
                </div>
                {form.letterhead_data_url && (
                  <div className="mt-3 border border-slate-200 p-3 rounded inline-block">
                    <img src={form.letterhead_data_url} alt="Letterhead preview" className="max-h-40 object-contain" />
                  </div>
                )}
                <div className="mt-4 max-w-xs">
                  <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600 block mb-1">Letterhead opacity ({Math.round(form.letterhead_opacity * 100)}%)</label>
                  <input type="range" min="0.03" max="0.4" step="0.01" value={form.letterhead_opacity} onChange={e => upd({ letterhead_opacity: Number(e.target.value) })} className="w-full" data-testid="tmpl-letterhead-opacity" />
                </div>
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-slate-600 mb-2">Accent Colour Override</div>
                <div className="flex items-center gap-3">
                  <input type="color" value={form.accent_color || "#002FA7"} onChange={e => upd({ accent_color: e.target.value })} className="w-14 h-10 border border-slate-300 rounded cursor-pointer" />
                  <input value={form.accent_color || ""} onChange={e => upd({ accent_color: e.target.value })} placeholder="Leave blank to use company default"
                    className="flex-1 rounded border border-slate-300 focus:border-[#002FA7] p-2 outline-none text-sm font-mono" />
                  {form.accent_color && <button onClick={() => upd({ accent_color: "" })} className="text-xs text-slate-500 hover:underline">Reset</button>}
                </div>
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-slate-600 mb-2">Show / Hide Sections</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    ["show_hsn", "HSN Column"],
                    ["show_discount", "Discount Column"],
                    ["show_bank", "Bank Details"],
                    ["show_signature", "Signature Block"],
                    ["show_amount_words", "Amount in Words"],
                    ["show_qr", "UPI QR Code"],
                  ].map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 text-sm px-3 py-2 border border-slate-200 rounded hover:bg-slate-50">
                      <input type="checkbox" checked={!!form[key]} onChange={e => upd({ [key]: e.target.checked })} data-testid={`tmpl-${key}`} />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === "preview" && <TemplatePreview form={form} />}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex justify-end gap-3 z-20">
          <button onClick={() => setEditor(null)} className="rounded border border-slate-300 px-4 py-2 hover:bg-slate-50 text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="tmpl-save" className="rounded bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2 font-bold flex items-center gap-2 text-sm disabled:opacity-60">
            <Check size={15} weight="bold" /> {saving ? "Saving…" : `${editor.mode === "create" ? "Create" : "Update"} Template`}
          </button>
        </div>
      </div>
    </div>
  );
};

/* -------- Live Preview -------- */
const TemplatePreview = ({ form }) => {
  const accent = form.accent_color || "#002FA7";
  return (
    <div className="border border-slate-200 bg-slate-50 p-6 rounded">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-3">Live Preview — sample data with your template</div>
      <div className="relative bg-white shadow-md p-8 mx-auto max-w-3xl" style={{ fontFamily: "Inter, Outfit, sans-serif" }}>
        {form.letterhead_data_url && (
          <img src={form.letterhead_data_url} alt="" aria-hidden="true"
            className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
            style={{ opacity: form.letterhead_opacity || 0.08 }} />
        )}
        <div className="relative">
          <div className="flex justify-between items-start pb-4 mb-5" style={{ borderBottom: `2px solid ${accent}` }}>
            <div>
              <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>SwifTech India</h1>
              <p className="text-[11px] text-slate-600 mt-0.5">Security System Marketplace</p>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{form.doc_type}</div>
              <div className="text-xl font-bold" style={{ color: accent, fontFamily: 'Outfit, sans-serif' }}>#SAMPLE-0001</div>
            </div>
          </div>

          {form.header_html && <div className="text-xs mb-3" {...safeHtmlProps(form.header_html)} />}

          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Bill To</div>
            <div className="text-sm font-bold">Acme Industries Pvt Ltd</div>
            <div className="text-xs text-slate-600">Mumbai, Maharashtra</div>
          </div>

          {form.subject_prefix && (
            <div className="mb-3 border-l-4 pl-2 text-sm" style={{ borderColor: accent }}>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Subject</div>
              <div>{form.subject_prefix}Sample Project</div>
            </div>
          )}

          <table className="w-full text-xs mb-4">
            <thead>
              <tr className="text-white" style={{ backgroundColor: accent }}>
                <th className="text-left p-1.5">Item</th>
                {form.show_hsn && <th className="text-center p-1.5">HSN</th>}
                <th className="text-right p-1.5">Qty</th>
                <th className="text-right p-1.5">Rate</th>
                {form.show_discount && <th className="text-right p-1.5">Disc %</th>}
                <th className="text-right p-1.5">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-200">
                <td className="p-1.5">HIKVISION 4MP Dome Camera</td>
                {form.show_hsn && <td className="p-1.5 text-center">85258010</td>}
                <td className="p-1.5 text-right">10</td>
                <td className="p-1.5 text-right">₹5,000</td>
                {form.show_discount && <td className="p-1.5 text-right">5</td>}
                <td className="p-1.5 text-right font-semibold">₹47,500</td>
              </tr>
            </tbody>
          </table>

          <div className="flex justify-end mb-4">
            <div className="w-64 text-xs">
              <div className="flex justify-between py-0.5"><span className="text-slate-500">Sub Total</span><span>₹47,500</span></div>
              <div className="flex justify-between py-0.5"><span className="text-slate-500">GST (18%)</span><span>₹8,550</span></div>
              <div className="flex justify-between py-1.5 bg-slate-100 px-2 border-y border-slate-800 font-bold"><span>Total</span><span>₹56,050</span></div>
            </div>
          </div>

          {form.show_amount_words && (
            <div className="text-[11px] border-t border-b border-slate-200 py-2 mb-4 italic">
              <span className="text-slate-500 font-bold not-italic mr-1">Words:</span>Fifty Six Thousand Fifty Rupees only
            </div>
          )}

          <div className="grid grid-cols-2 gap-6 text-xs mb-4">
            {form.notes_html && (
              <div>
                <div className="uppercase tracking-wider text-slate-500 font-bold mb-1">Notes</div>
                <div {...safeHtmlProps(form.notes_html)} />
              </div>
            )}
            {form.terms_html && (
              <div>
                <div className="uppercase tracking-wider text-slate-500 font-bold mb-1">Terms &amp; Conditions</div>
                <div {...safeHtmlProps(form.terms_html)} />
              </div>
            )}
          </div>

          {form.show_bank && (
            <div className="border border-slate-200 bg-slate-50 p-2 text-[11px] mb-4">
              <div className="uppercase tracking-wider text-slate-500 font-bold mb-0.5">Payment Details</div>
              <div>Bank: ICICI · A/C: 1234 5678 9012 · IFSC: ICIC0001234 · UPI: swiftech@icici</div>
            </div>
          )}

          {form.show_signature && (
            <div className="flex justify-end mt-6">
              <div className="text-center border-t-2 border-slate-400 pt-1 px-8 text-[11px]">
                <div className="font-bold">Authorised Signatory</div>
                <div className="text-slate-500 uppercase tracking-wider text-[9px]">For SwifTech India</div>
              </div>
            </div>
          )}

          {form.footer_html && (
            <div className="mt-5 pt-3 border-t border-slate-200 text-[11px] text-slate-600" {...safeHtmlProps(form.footer_html)} />
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplatesTab;
